<template>
    <div class="column-settings" :style="{ maxHeight: maxHeight + 'px' }">
        <template v-if="showColumnsList">
            <div class="column-settings__title">Колонки таблицы</div>
            <!-- Список — единственная прокручиваемая часть панели: заголовок, текстовые
                 настройки и футер остаются на месте, когда колонок больше, чем влезает
                 по высоте (сейчас так и есть даже после удаления «Последнего сообщения»). -->
            <div class="column-settings__list">
                <!-- № вынесен из Draggable: его нельзя ни переставить, ни уронить поверх него
                     другую колонку — закрепление отсчитывается от начала строки. -->
                <div class="column-settings__item" :key="item.key" v-for="item in lockedConfig">
                    <span class="column-settings__grip-space" aria-hidden="true">⋮⋮</span>
                    <span class="column-settings__name">{{ titles[item.key] }}</span>
                    <button
                        type="button"
                        class="column-settings__pin"
                        :class="{ 'column-settings__pin_on': item.pinned }"
                        :title="pinTitle(item)"
                        @click="togglePin(item)"
                    >
                        <PinSvg></PinSvg>
                    </button>
                    <VSwitchButton color="#00a55a" disabled v-model="item.visible"></VSwitchButton>
                </div>
                <Draggable v-model="localConfig" handle=".column-settings__grip" :move="onMove">
                    <div class="column-settings__item" :key="item.key" v-for="item in localConfig">
                        <span class="column-settings__grip">⋮⋮</span>
                        <span class="column-settings__name" :class="{ 'column-settings__name_dim': !item.visible }">
                            {{ titles[item.key] }}
                        </span>
                        <button
                            type="button"
                            class="column-settings__pin"
                            :class="{ 'column-settings__pin_on': item.pinned }"
                            :title="pinTitle(item)"
                            @click="togglePin(item)"
                        >
                            <PinSvg></PinSvg>
                        </button>
                        <VSwitchButton
                            color="#00a55a"
                            :disabled="isRequired(item.key)"
                            v-model="item.visible"
                        ></VSwitchButton>
                    </div>
                </Draggable>
            </div>
        </template>

        <!-- Не колонки — отдельные опции, поэтому не в списке выше (ни в Draggable, ни в
             VSwitchButton-строке): другой тип контрола (сегментированный переключатель, а
             не тумблер видимости) плюс разделитель — визуально это другая категория настроек. -->
        <div
            class="column-settings__text-options"
            :class="{ 'column-settings__text-options_bordered': showColumnsList }"
        >
            <div class="column-settings__option">
                <div class="column-settings__option-title">Строк текста</div>
                <div class="column-settings__segmented">
                    <button
                        type="button"
                        class="column-settings__segmented-btn column-settings__lines-btn"
                        :class="{ 'column-settings__segmented-btn_on': textLines === 1 }"
                        @click="$emit('update-text-lines', 1)"
                    >
                        1 строка
                    </button>
                    <button
                        type="button"
                        class="column-settings__segmented-btn column-settings__lines-btn"
                        :class="{ 'column-settings__segmented-btn_on': textLines === 3 }"
                        @click="$emit('update-text-lines', 3)"
                    >
                        3 строки
                    </button>
                </div>
            </div>

            <div class="column-settings__option">
                <div class="column-settings__option-title">Текст колонки</div>
                <div class="column-settings__segmented">
                    <button
                        type="button"
                        class="column-settings__segmented-btn column-settings__source-btn"
                        :class="{ 'column-settings__segmented-btn_on': textColumnSource === 'description' }"
                        @click="$emit('update-text-column-source', 'description')"
                    >
                        Описание
                    </button>
                    <button
                        type="button"
                        class="column-settings__segmented-btn column-settings__source-btn"
                        :class="{ 'column-settings__segmented-btn_on': textColumnSource === 'last-message' }"
                        @click="$emit('update-text-column-source', 'last-message')"
                    >
                        Последнее сообщение
                    </button>
                </div>
            </div>
        </div>

        <div class="column-settings__footer" v-if="showColumnsList">
            <button class="column-settings__reset" type="button" @click="onReset">Сбросить</button>
            <VButton class="button_green column-settings__save" @click="onSave" v-if="isDirty">Сохранить</VButton>
        </div>
    </div>
</template>
<script>
import Draggable from 'vuedraggable';
import VButton from '@/views/components/VButton/VButton.vue';
import VSwitchButton from '@/views/components/VCheckbox/VSwitchButton.vue';
import PinSvg from '@/images/pin.svg';
import { COLUMNS, defaultConfig, serializeConfig, pinnedFirst, isOrderLocked, isRequired } from '@/js/tableColumns';

export default {
    components: { Draggable, VButton, VSwitchButton, PinSvg },
    props: {
        config: { type: Array, required: true },
        showColumnsList: { type: Boolean, default: true },
        textLines: { type: Number, required: true },
        textColumnSource: { type: String, required: true },
    },
    data() {
        const config = this.config.map((item) => ({ ...item }));

        return {
            lockedConfig: config.filter((item) => isOrderLocked(item.key)),
            localConfig: config.filter((item) => !isOrderLocked(item.key)),
            // 80vh из вёрстки — потолок для больших экранов. На маленьких этого мало:
            // кнопка «Колонки» открывает панель вниз от себя, а не от верха окна, и на
            // невысоком окне 80vh запросто выходит за нижний край — там не работает даже
            // прокрутка страницы (родитель её обрезает), футер с «Сохранить» становится
            // недостижим. mounted() пересчитывает потолок под фактическое место открытия.
            maxHeight: null,
        };
    },
    computed: {
        // Список разрезан на два, а наружу уходит и сравнивается целый.
        fullConfig() {
            return pinnedFirst([...this.lockedConfig, ...this.localConfig]);
        },
        isDirty() {
            return serializeConfig(this.fullConfig) !== serializeConfig(this.config);
        },
        // Опора цепочки: пока она не закреплена, закреплять остальные бессмысленно.
        anchorPinned() {
            const anchor = this.lockedConfig[0];

            return anchor ? Boolean(anchor.pinned) : true;
        },
        // Название колонки «Описание» в списке должно совпадать с тем, что реально
        // покажет колонка — иначе список настроек лжёт сам себе (в реестре COLUMNS
        // у неё статичный заголовок-фолбэк, он не знает о выбранном источнике текста).
        textColumnTitle() {
            return this.textColumnSource === 'last-message' ? 'Последнее сообщение' : 'Описание';
        },
        // Вычисляемый, а не разовый в data(): панель не закрывается при переключении
        // текстовых опций, и название колонки в списке обязано меняться на глазах.
        titles() {
            return COLUMNS.reduce((acc, column) => {
                acc[column.key] = column.key === 'description' ? this.textColumnTitle : column.title;
                return acc;
            }, {});
        },
    },
    methods: {
        isRequired,
        pinTitle(item) {
            if (item.pinned) return 'Открепить';

            return isOrderLocked(item.key) || this.anchorPinned
                ? 'Закрепить при прокрутке'
                : 'Закрепить при прокрутке — вместе с №';
        },
        // Перетаскивание разрешено только внутри своей группы: закреплённые колонки
        // обязаны идти первыми, и бросок через границу всё равно был бы отменён.
        onMove(event) {
            const related = event.relatedContext.element;

            // Когда курсор над самим контейнером списка, а не над строкой, vuedraggable
            // отдаёт контекст без element — без проверки обращение к нему падает.
            if (!related) return false;

            return Boolean(event.draggedContext.element.pinned) === Boolean(related.pinned);
        },
        togglePin(item) {
            item.pinned = !item.pinned;

            // Цепочка отсчитывается от начала строки, поэтому закрепление любой колонки
            // втягивает в неё №, а открепление № распускает всю цепочку за ним.
            if (isOrderLocked(item.key)) {
                if (!item.pinned) {
                    this.localConfig.forEach((other) => {
                        other.pinned = false;
                    });
                }
            } else if (item.pinned) {
                this.lockedConfig.forEach((anchor) => {
                    anchor.pinned = true;
                });
            }

            // Пересортировка сразу: колонка переезжает в свою группу на глазах, а не
            // прыгает после сохранения, когда порядок поправит pinnedFirst.
            this.localConfig = pinnedFirst(this.localConfig);
        },
        onReset() {
            const config = defaultConfig();

            this.lockedConfig = config.filter((item) => isOrderLocked(item.key));
            this.localConfig = config.filter((item) => !isOrderLocked(item.key));
        },
        onSave() {
            this.$emit(
                'save',
                this.fullConfig.map((item) => ({ ...item })),
            );
        },
        // Потолок высоты под фактическое место открытия: this.$el стоит уже там, где
        // будет висеть (top у него фиксирован CSS-ом и от собственной высоты не зависит),
        // поэтому расстояние до низа окна можно мерить сразу после монтирования.
        updateMaxHeight() {
            const margin = 16;
            const top = this.$el.getBoundingClientRect().top;
            const fitsToViewportBottom = window.innerHeight - top - margin;

            // 80vh остаётся потолком сверху: на высоком окне и с кнопкой у самого верха
            // fitsToViewportBottom почти равен window.innerHeight — без него панель
            // растянулась бы во весь экран, а не смотрелась бы аккуратным поповером.
            this.maxHeight = Math.max(200, Math.min(fitsToViewportBottom, window.innerHeight * 0.8));
        },
    },
    mounted() {
        this.updateMaxHeight();
        window.addEventListener('resize', this.updateMaxHeight);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateMaxHeight);
    },
};
</script>
<style lang="scss">
.column-settings {
    // Панель целиком из текста: ширина растёт вместе с размером текста (js/textSize.js),
    // иначе в крупном режиме названия колонок и кнопки подвала упирались бы в 270px.
    width: calc(270px * var(--text-scale, 1));
    max-width: calc(100vw - 32px);
    // Реальный потолок приходит инлайн-стилем из updateMaxHeight() — он учитывает, где
    // на экране открылась панель, а не только высоту окна. 80vh здесь — запасной вариант
    // на момент между вставкой в DOM и первым mounted() (виден на очень медленном кадре)
    // и подстраховка на случай, если getBoundingClientRect() почему-то не отработает.
    max-height: 80vh;
    background-color: #fff;
    border-radius: 14px;
    box-shadow: 0 10px 30px rgba(44, 44, 44, 0.14);
    padding: 16px 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    overflow: hidden;
}

// Прокручивается только список: заголовок, текстовые настройки и футер — не часть
// потока, который растягивает панель сверх max-height, а фиксированные края вокруг него.
.column-settings__list {
    overflow-y: auto;
    // 0, а не auto: без этого flex-элемент не сжимается своим содержимым (виновник —
    // умолчание min-height: auto у flex-детей), max-height родителя не работал бы,
    // список выталкивал бы панель за пределы экрана вместо появления полосы прокрутки.
    min-height: 0;
    flex: 1 1 auto;
}

.column-settings__title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #2c2c2c;
    padding: 0 4px;
}

.column-settings__item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 4px;
}

.column-settings__grip {
    color: #ccc;
    font-size: 12px;
    user-select: none;
    flex-shrink: 0;
    // Перетаскивание навешено на handle: курсор показываем только на нём.
    cursor: grab;

    &:active {
        cursor: grabbing;
    }
}

.column-settings__pin {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: none;
    color: #c9ced3;
    cursor: pointer;

    svg {
        width: 13px;
        height: 13px;
    }

    &:hover {
        background: #f1f3f4;
        color: #5f6368;
    }

    // Зелёный держится и на наведении: базовое правило :hover перекрасило бы
    // закреплённую булавку в серый, будто закрепление снялось.
    &_on,
    &_on:hover {
        color: #00a55a;
    }
}

// Место ручки у закреплённой строки: рисовать неработающую ручку нельзя, а без
// распорки название уехало бы левее остальных. Отдельный класс, а не скрытая ручка, —
// Sortable ищет handle по селектору и подхватил бы её даже пустой.
.column-settings__grip-space {
    flex-shrink: 0;
    font-size: 12px;
    visibility: hidden;
}

.column-settings__name {
    font-size: 0.8125rem;
    color: #2c2c2c;
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &_dim {
        color: #999;
    }
}

// Обёртка вокруг ОБЕИХ текстовых опций («Строк текста» и «Текст колонки») — разделитель
// от списка колонок один на двоих, а не дублируется у каждой опции по отдельности.
.column-settings__text-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 8px 4px 0;

    &_bordered {
        margin-top: 4px;
        padding-top: 12px;
        border-top: 1px solid #e5e5e5;
    }
}

.column-settings__option-title {
    margin-bottom: 8px;
    font-size: 0.8125rem;
    color: #2c2c2c;
}

// Тот же визуальный язык, что у переключателя Таблица/Карточки (display-switch в
// RequestStore.vue): пилюля с двумя кнопками. Сюда не импортирую его классы — компоненты
// не связаны, а два инстанса одного паттерна дешевле лишней кросс-компонентной зависимости.
.column-settings__segmented {
    display: flex;
    gap: 2px;
    padding: 3px;
    border: 1px solid #e3e5e8;
    border-radius: 11px;
}

.column-settings__segmented-btn {
    flex: 1;
    border: 0;
    background: transparent;
    border-radius: 8px;
    padding: 6px 13px;
    font: 500 0.8125rem var(--font-family);
    color: #5f6368;
    cursor: pointer;

    &:hover {
        background: #f6f7f8;
        color: #3c4043;
    }

    &_on {
        // Заливка активного состояния (#e8f5e9) нигде в проекте не вынесена в
        // токен/CSS-переменную — она захардкожена везде, где встречается
        // (RequestStore.vue, RequestStatsBar.vue, BaseToolsSort.vue и др.).
        // Дублирую тем же хардкодом, а не завожу первый в проекте токен под одну кнопку.
        background: #e8f5e9;
        color: var(--color-green);
        font-weight: 600;
    }
}

.column-settings__lines-btn {
    white-space: nowrap;
}

// «Последнее сообщение» не помещается в половину 270px-панели одной строкой —
// вместо anywhere-обрезки или уменьшения кегля разрешаем перенос на вторую строку:
// это кнопка-переключатель, а не колонка таблицы, двум строкам тут ничего не мешает.
.column-settings__source-btn {
    white-space: normal;
    text-align: center;
    line-height: 1.2;
}

.column-settings__footer {
    display: flex;
    gap: 8px;
    padding-top: 8px;
    border-top: 1px solid #e5e5e5;
}

.column-settings__reset {
    flex: 1;
    padding: 8px 12px;
    background-color: transparent;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.8125rem;
    color: #d9641e;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background-color: #f5f5f5;
        border-color: #d9641e;
    }

    &:active {
        background-color: #efefef;
    }
}

// Обе кнопки делят подвал поровну: у «Сохранить» размеры выставляются здесь,
// а не через button_small — тот даёт другую высоту, и кнопки не встают в один ряд.
.column-settings__save {
    flex: 1;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 0.8125rem;
    line-height: 1rem;
    box-shadow: none;
}
</style>
